import {
  type AccountData,
  type LocationAutocompleteResult,
  type LocationSearchItem,
  getAccountResidentName,
  isEmpty,
  isEmptyAll,
  isNotEmpty,
} from '@terros/common'
import { stringifyAccountAddressWithUnit } from '@front/model'
import { rankSearchResults } from './searchResultRanker'

export type AccountResultAccount = Pick<
  AccountData,
  | 'accountId'
  | 'location'
  | 'resident'
  | 'homeowner'
  | 'statusId'
  | 'workflowStageId'
  | 'workflowActionId'
  | 'owner'
  | 'ownerId'
  | 'workflowHistory'
  | 'tags'
  | 'ownerHistory'
>

/** Required keys. A missing resident or location is undefined, not an omitted field. */
export type CompactAccountResultAccount = Omit<AccountResultAccount, 'resident' | 'location'> & {
  resident: AccountData['resident']
  location: AccountData['location']
}

/** AccountData declares resident and location. Either value may be undefined. */
export type CompactAccountResultInput = AccountData | CompactAccountResultAccount

export type AccountClickHandler = (account: AccountData, options?: { resultRank?: number }) => void

export type PlaceClickHandler = (place: LocationAutocompleteResult) => void

export type AccountResultText = {
  label: string
  details: string | undefined
}

export type AccountSearchResult = {
  type: 'Account'
  account: AccountData
  primaryText: string
  secondaryText?: string
}

export type PlaceSearchResult = {
  type: 'Location'
  place: LocationAutocompleteResult
  primaryText: string
  secondaryText?: string
}

export type SearchResult = AccountSearchResult | PlaceSearchResult

type BuildSearchResultsParams = {
  accounts: AccountData[]
  places: LocationSearchItem[]
  query: string
  isPlaceSearchEnabled: boolean
}

const MAX_PLACE_RESULTS = 5
export const MAX_ACCOUNT_RESULTS = 20

export function getAccountResultText(account: AccountResultAccount, fallbackLabel?: string): AccountResultText {
  const address = getAccountAddress(account, fallbackLabel)
  if (isEmptyAll([account.resident?.firstName, account.resident?.lastName]))
    return { label: address, details: undefined }
  return { label: getAccountResidentName(account.resident), details: address }
}

export function getResidentFirstAccountText(
  account: AccountResultAccount,
  unknownResidentLabel: string
): AccountResultText {
  return {
    label: getResidentFirstLabel(account, unknownResidentLabel),
    details: getAccountAddress(account),
  }
}

function getResidentFirstLabel(account: AccountResultAccount, unknownResidentLabel: string): string {
  const primaryResidentName = getAccountResidentName(account.resident)
  if (isNotEmpty(primaryResidentName)) return primaryResidentName
  const possibleResidentName = getAccountResidentName(account.homeowner)
  if (isNotEmpty(possibleResidentName)) return possibleResidentName
  return unknownResidentLabel
}

function getAccountAddress(account: AccountResultAccount, fallbackLabel?: string): string {
  if (isEmpty(account.location)) return fallbackLabel ?? account.accountId
  return stringifyAccountAddressWithUnit(account.location)
}

export function buildSearchResults({
  accounts,
  places,
  query,
  isPlaceSearchEnabled,
}: BuildSearchResultsParams): SearchResult[] {
  const accountResults = accounts.slice(0, MAX_ACCOUNT_RESULTS).map(toAccountSearchResult)
  if (!isPlaceSearchEnabled) return accountResults
  const placeResults = places.slice(0, MAX_PLACE_RESULTS).map(toPlaceSearchResult)
  return rankSearchResults<SearchResult>({ results: [...accountResults, ...placeResults], query })
}

function toAccountSearchResult(account: AccountData): AccountSearchResult {
  const { label, details } = getAccountResultText(account)
  return { type: 'Account', account, primaryText: label, secondaryText: details }
}

function toPlaceSearchResult(place: LocationSearchItem): PlaceSearchResult {
  return {
    type: 'Location',
    place: { placeId: place.placeId, text: place.description },
    primaryText: place.description,
    secondaryText: place.subtitle,
  }
}

export function getSearchResultKey(result: SearchResult): string {
  if (result.type === 'Location') return `${result.place.placeId}-${result.place.text}`
  return result.account.accountId
}
